import { apiClient } from "@/api/client";
import {
  RestoreResponse,
  RestoreResultData,
  toRestoreError,
} from "@/api/restore";
import { Options } from "@/components/ui/DropDown";
import useDashboardStore, {
  CampaignData,
  CampaignDetailResponse,
} from "@/pages/dashboard/store/dashboardStore";
import { Team } from "@/pages/teams/store/teamsStore";
import { AxiosError } from "axios";
import { create } from "zustand";

/**
 * Pull a user-facing message out of an API error.
 *
 * The API is inconsistent about where the text lives: most failures use
 * `error`, while the 423 lock responses (`FLIGHT_LOCKED` / `ASSET_LOCKED`) use
 * `message`. Checking only one field turned a specific, actionable reason into
 * "Something went wrong", so both are read here.
 */
const resolveApiErrorMessage = (
  axiosError: AxiosError<any>,
  subject: "flight" | "asset",
): string => {
  const data = axiosError.response?.data;
  if (!data) return "Something went wrong";

  const message = data.message || data.error;

  // Locked responses are worth a nudge toward the resolution, since the raw
  // message explains the cause but not what the user can do next.
  if (data.code === "FLIGHT_LOCKED" || data.code === "ASSET_LOCKED") {
    return (
      (message ||
        `This ${subject} is locked because related creative work has been briefed.`) +
      " You can still delete it, but editing is no longer allowed."
    );
  }

  return message || "Something went wrong";
};

export interface LanguageResponse {
  data: Language[];
}

export interface Language {
  id: string;
  name: string;
}

export interface TeamsRespose {
  data: Array<Team>;
}

interface Objective {
  id: string;
  name: string;
  desc: string;
}

interface AdCategory {
  id: string;
  name: string;
}

interface ObjectiveResponse {
  success: boolean;
  data: Objective[];
}

interface AdCategoryResponse {
  success: boolean;
  data: AdCategory[];
}

// Updated Assets API interfaces
export interface AssetsPageData {
  flight_id: string;
  flight_number: number;
  flight_start_date: string;
  flight_end_date: string;
  languages: AssetsLanguage[];
}

export interface AssetsLanguage {
  language_id: string;
  language_name: string;
  objectives: AssetsObjective[];
}

export interface AssetsObjective {
  objective_id: string;
  objective_name: string;
  locations: AssetsLocation[];
}

export interface AssetsLocation {
  region: string;
  flight_rows: {
    [flightRowId: string]: AssetsFlightRow;
  };
}

export interface AssetsFlightRow {
  flight_row_id: string;
  flight_row_number: string;
  ad_categories: AssetsAdCategory[];
}

export interface AssetsAdCategory {
  flight_ad_categories_id: string;
  ad_category_id: string;
  ad_category_name: string;
}

export interface AssetsPageResponse {
  success: boolean;
  data: AssetsPageData[];
}

// New interfaces for parameter definitions
export interface ParameterField {
  id: string;
  key: string;
  label: string;
  type: string;
  placeholder: string;
  is_required: boolean;
  is_multiple: boolean;
  defaultValue: any;
  options: Array<{ title: string; value: string }> | null;
  extra_meta: any;
  group_name: string | null;
  sort_order: number;
  parameter_value_type: string | null;
  parameter_value: any;
}

export interface ParameterDefinition {
  id: string;
  key: string;
  label: string;
  sort_order: number;
  group_name: string | null;
  extra_meta: any;
  fields: ParameterField[];
}

export interface ParameterDefinitions {
  broadcast: ParameterDefinition[];
  digital: ParameterDefinition[];
  outdoor_digital: ParameterDefinition[];
  print: ParameterDefinition[];
  social: ParameterDefinition[];
}

export interface ParameterVariantField {
  parameter_field_id: string;
  key: string;
  label: string;
  type: string;
  options: any;
  value: any;
}

export interface ParameterVariant {
  publisher_id: string;
  ad_type_id: string;
  media_type: string;
  value_type: "user_defined" | "single_publisher" | "multi_publisher";
  fields: ParameterVariantField[];
}

export interface UIParameter {
  key: string;
  label: string;
  media_type: string;
  input_type: string;
  definition_id: string;
  ui_schema: {
    allow_multiple: boolean;
  };
  variants: ParameterVariant[];
}

export interface UIAdType {
  id: string;
  name: string;
  media_type_id: string;
  media_type: string;
  parameters: UIParameter[];
}

export interface UIParametersByAdType {
  [adCategoryId: string]: UIAdType[];
}

export interface AdCategoryDataItem {
  ad_category_id: string;
  ad_category_name: string;
  versions: number;
  publishers: Array<{
    id: string;
    name: string;
    type: "single" | "multi";
  }>;
  ad_types: Array<{
    id: string;
    name: string;
    publisher_id: string;
  }>;
}

export interface AdCategoryDataResponse {
  success: boolean;
  data: AdCategoryDataItem[];
  parameter_definitions: ParameterDefinitions;
  ui_parameters_by_ad_type: UIParametersByAdType;
}

// Assets Store Interface
interface AssetsStoreRequest {
  campaign_id: string;
  data: AssetsFlightData[];
}

interface AssetsFlightData {
  flight_id: string;
  flight_rows: AssetsStoreFlightRow[];
}

interface AssetsStoreFlightRow {
  flight_row_id: string;
  flight_ad_categories: AssetsFlightAdCategory[];
}

interface AssetsFlightAdCategory {
  flight_ad_categories_id: string;
  ad_rows: AssetsAdRow[];
}

interface AssetsAdRow {
  ad_row_id: string;
  versions?: number;
  ad_type_id?: string;
  ad_category_id?: string;
  publisher_id?: string;
  parameters: AssetsParameter[];
}

interface AssetsParameter {
  parameter_definition_id: string;
  parameter_key: string;
  parameter_value: string;
}

//

interface CampaignRequest {
  name: string;
  budget: number;
  project_code?: string;
  file_naming_convention: string;
  client_team_id: string;
  team_ids: string[];
  user_ids?: string[];
  language_ids: string[];
}

interface FlightsUpdateRequest {
  flights: FlightUpdateRequest[];
}

interface FlightUpdateRequest {
  flight_number: number;
  start_date?: string;
  end_date?: string;
  rows: FlightRowUpdateRequest[];
}

interface FlightRowUpdateRequest {
  id?: string;
  region?: string;
  ad_category?: string[];
}

export interface FlightsRequest {
  flights: FlightRequest[];
}

export interface FlightRequest {
  campaign_id: string;
  flight_number: number;
  start_date?: string;
  end_date?: string;
  rows: FlightRowRequest[];
}

export interface FlightRowRequest {
  flight_row_number?: number;
  language_id?: string;
  objective_id?: string;
  region?: string;
  ad_category?: string[];
}

interface FlightResponse {
  success: boolean;
  data: FlightData;
}

interface FlightGetResponse {
  success: boolean;
  data: Array<FlightData>;
}

interface FlightData {
  id: string;
  campaign_id: string;
  created_by: string;
  flight_number: number;
  start_date: string;
  end_date: string;
  updated_at: string;
  created_at: string;
  rows: FlightRow[];
}

interface FlightRow {
  id: string;
  campaign_id: string;
  flight_id: string;
  created_by: string;
  language_id: string;
  objective_id: string;
  region: string;
  updated_at: string;
  created_at: string;
  ad_category: AdCategory[];
}

interface AdCategory {
  id: string;
  campaign_id: string;
  flight_row_id: string;
  ad_category_id: string;
  created_by: string;
  updated_at: string;
  created_at: string;
}

//Assets Value Interface
/**
 * The campaign's saved Boxes, as `GET /assets/ad-categories?campaign_id=` reports them.
 *
 * A **list** per ad category, one entry per Box. It used to nest
 * `ad_types[adTypeId].publishers[publisherId]`, which held a single Box per
 * (category, ad type, publisher) for the whole campaign and silently dropped the rest —
 * so a second Box of the same ad type in another flight row disappeared from the wizard.
 */
export interface ExistingAssetsValues {
  [adCategoryId: string]: {
    assets: ExistingAssetEntry[];
  };
}

export interface ExistingAssetEntry {
  asset_meta: ExistingAssetMeta;
  parameters: Array<{
    id: string;
    assets_row_id: string;
    parameter_definition_id: string;
    parameter_key: string;
    value: string;
    value_json: {
      key: string;
      value: string;
    };
    created_at: string;
  }>;
}

export interface ExistingAssetMeta {
  /** The `assets_rows` primary key — what endpoints address a Box by. */
  asset_id: string;
  /**
   * The client-generated id the wizard first sent, persisted so the update path can match
   * a payload row back to its saved row. Not the row's identity; see `asset_id`.
   */
  assets_row_id: string;
  versions: number;
  flight_row_id: string;
  flight_ad_categories_id: string;
  ad_type_id: string;
  ad_category_id: string;
  publisher_id: string;
  parameter_value_pair: {
    ad_row_id: string;
    ad_type_id: string;
    parameters: Array<{
      id: string;
      key: string;
      value: string;
      definition_id: string;
    }>;
    publisher_id: string;
    ad_category_id: string;
  };
}

// Campaign Summary Interfaces
export interface CampaignSummaryData {
  campaign: {
    id: string;
    name: string;
    status: string;
    progress_status: string;
    budget: number;
    created_at: string;
  };
  teams: Array<{
    id: string;
    name: string;
    team_type: string;
  }>;
  users: any[];
  flights: Array<{
    id: string;
    flight_number: number;
    start_date: string;
    end_date: string;
  }>;
  stats: {
    total_flights: number;
    total_flight_rows: number;
    total_assets: number;
    unique_languages: number;
    unique_publishers: number;
    unique_ad_types: number;
    total_teams: number;
    total_users: number;
  };
  assets_preview: Array<{
    language: string;
    objective: string;
    region: string;
    category: string;
    publisher: string;
    ad_type: string;
    due_date: string | null;
  }>;
}

export interface CampaignSummaryResponse {
  success: boolean;
  message: string;
  data: CampaignSummaryData;
}

export interface AssetsTableRow {
  id: string;
  language: string;
  objective: string;
  region: string;
  category: string;
  publisher: string;
  ad_type: string;
  due_date: string;
}

export interface AssetsTableResponse {
  data: AssetsTableRow[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: Array<{
      url: string | null;
      label: string;
      active: boolean;
    }>;
    path: string;
    per_page: [number, number];
    to: number;
    total: number;
    campaign_id: string;
    with: string[];
    current_flight_id: string;
    available_flights: Array<{
      id: string;
      period: string;
    }>;
    sort: {
      column: string;
      direction: string;
    };
  };
}

interface CampaignState {
  clientOptions: Array<Options>;
  teamOptions: Array<Options>;
  userOptions: Array<Options>;
  userTeamMap: Record<string, string>;
  languageOptions: Array<Options>;
  objectiveOptions: Array<Objective>;
  adCategoryOptions: Array<AdCategory>;
  campaign: CampaignData | null;
  assetsPageData: AssetsPageData[];
  adCategoryData: AdCategoryDataItem[];
  parameterDefinitions: ParameterDefinitions | null;
  uiParametersByAdType: UIParametersByAdType | null;
  assetsValues: ExistingAssetsValues | null;

  campaignSummary: CampaignSummaryData | null;
  assetsTableData: AssetsTableRow[];
  assetsTableMeta: AssetsTableResponse["meta"] | null;

  getClientOptions: () => Promise<void>;
  getTeamOptions: () => Promise<void>;
  getUserOptions: () => Promise<void>;
  getLanguageOptions: () => Promise<void>;
  createCampaign: (campaignData: CampaignRequest) => Promise<CampaignData>;
  updateCampaign: (
    campaignData: CampaignRequest,
    id: string
  ) => Promise<CampaignData>;
  getSingleCampaign: (id: string) => Promise<void>;

  getObjectivesForFlight: () => Promise<Array<Objective>>;
  getAdCategoryForFlight: () => Promise<Array<AdCategory>>;
  createFlight: (flightData: FlightsRequest) => Promise<FlightData>;
  getFlightData: (id: string) => Promise<Array<FlightData>>;
  updateFlight: (
    flightData: FlightsUpdateRequest,
    id: string
  ) => Promise<FlightData>;
  getAssetsPageData: (campaignId: string) => Promise<AssetsPageData[]>;
  getAdCategoryData: (campaignId?: string) => Promise<void>;
  createAssets: (assetsData: AssetsStoreRequest) => Promise<void>;
  updateAssets: (assetsData: AssetsStoreRequest) => Promise<void>;
  deleteFlight: (flightId: string) => Promise<void>;
  deleteAsset: (assetId: string) => Promise<void>;
  // Undo of a delete. Rejects with a RestoreError carrying the server's
  // `error_code`, so "already restored" (404 NO_RESTORABLE_DELETION) and a
  // blocking ancestor (409) can be told apart from a genuine failure.
  restoreFlight: (flightId: string) => Promise<RestoreResultData>;
  restoreAsset: (assetId: string) => Promise<RestoreResultData>;

  getCampaignSummary: (campaignId: string) => Promise<CampaignSummaryData>;
  getAssetsTable: (
    campaignId: string,
    flightId?: string,
    sortColumn?: string,
    sortDirection?: string,
    page?: number
  ) => Promise<AssetsTableResponse>;

  downloadDeliveryTemplate: (campaignId: string) => Promise<void>;
  downloadBaseTemplate: () => Promise<void>;
  downloadCampaignAssets: (campaignId: string) => Promise<void>;
  parseCampaignTemplate: (file: File) => Promise<ParsedTemplate>;

  briefCampaignAssets: (campaignId: string) => Promise<BriefCampaignResult>;
}

export interface BriefCampaignResult {
  campaign_id: string;
  transitioned: number;
  transitioned_ids: string[];
}

export interface BriefCampaignResponse {
  success: boolean;
  message: string;
  data: BriefCampaignResult;
}

export interface ParsedTemplate {
  values: {
    campaign_name: string;
    project_code: string;
    budget: number | null;
  };
  resolved: {
    client_team_id: string | null;
    language_ids: string[];
    user_ids: string[];
  };
  unresolved: {
    client_name: string | null;
    languages: string[];
    members: string[];
  };
}

const useCampaignStore = create<CampaignState>((set, get) => ({
  clientOptions: [],
  teamOptions: [],
  userOptions: [],
  userTeamMap: {},
  languageOptions: [],
  objectiveOptions: [],
  adCategoryOptions: [],
  campaign: null,
  assetsPageData: [],
  adCategoryData: [],
  parameterDefinitions: null,
  uiParametersByAdType: null,
  assetsValues: null,

  campaignSummary: null,
  assetsTableData: [],
  assetsTableMeta: null,

  getClientOptions: async () => {
    try {
      const response = await apiClient.get<TeamsRespose>("/client-teams");
      const data = response.data;
      const options = data?.data?.map((team) => ({
        title: team.name,
        value: team.id,
      }));

      set({ clientOptions: options });
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getTeamOptions: async () => {
    try {
      const response = await apiClient.get<TeamsRespose>("/non-client-teams");
      const data = response.data;
      const options = data?.data?.map((team) => ({
        title: team.name,
        value: team.id,
      }));

      set({ teamOptions: options });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getUserOptions: async () => {
    try {
      const response = await apiClient.get<any>("/all-users-media-agency");
      const users = response.data?.data?.users ?? [];

      const options: Options[] = [];
      const userTeamMap: Record<string, string> = {};

      users.forEach((user: any) => {
        const fullName =
          `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() ||
          user.email ||
          "Unknown";

        // Pick a non-client team for this user (campaign members are non-client)
        const team =
          (user.teams ?? []).find((t: any) => t.team_type !== "client") ??
          (user.teams ?? [])[0];

        if (team?.id) {
          userTeamMap[user.id] = team.id;
        }

        options.push({ title: fullName, value: user.id });
      });

      set({ userOptions: options, userTeamMap });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getLanguageOptions: async () => {
    try {
      const response = await apiClient.get<LanguageResponse>("/languages");
      const data = response.data;

      const options = data?.data?.map((language) => ({
        title: language.name,
        value: language.id,
      }));

      set({ languageOptions: options });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  createCampaign: async (campaignData: CampaignRequest) => {
    try {
      const response = await apiClient.post("/campaigns", campaignData);
      const data: CampaignData = response.data.data;
      useDashboardStore.getState().addCampaignData(data);
      set({ campaign: data });

      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  updateCampaign: async (campaignData: CampaignRequest, id: string) => {
    try {
      const response = await apiClient.put(`/campaigns/${id}`, campaignData);
      const data: CampaignData = response.data.data;
      useDashboardStore.getState().addCampaignData(data);
      set({ campaign: data });

      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getSingleCampaign: async (id: string) => {
    try {
      const response = await apiClient.get<CampaignDetailResponse>(
        `/campaigns/${id}`
      );
      const data = response.data;

      set({ campaign: data.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  //Flight Page API

  getObjectivesForFlight: async () => {
    try {
      const response = await apiClient.get<ObjectiveResponse>(
        "/list-all-objectives"
      );
      const data: Array<Objective> = response.data.data;
      set({
        objectiveOptions: data,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getAdCategoryForFlight: async () => {
    try {
      const response = await apiClient.get<AdCategoryResponse>(
        "/list-ad-categories"
      );
      const data: Array<AdCategory> = response.data.data;
      set({
        adCategoryOptions: data,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  createFlight: async (flightData: FlightsRequest) => {
    try {
      const response = await apiClient.post<FlightResponse>(
        "/flights",
        flightData
      );
      const data: FlightData = response.data.data;
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getFlightData: async (id: string) => {
    try {
      const response = await apiClient.get<FlightGetResponse>(
        `/campaigns/${id}/flights`
      );
      const data: Array<FlightData> = response.data.data;
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  updateFlight: async (flightData: FlightsUpdateRequest, id: string) => {
    try {
      const response = await apiClient.put<FlightResponse>(
        `/flights/${id}`,
        flightData
      );
      const data: FlightData = response.data.data;
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      // A 423 lock response carries its reason in `message`, not `error`, so
      // reading only `error` reduced "This flight is locked because related
      // creative work has been briefed." to a generic failure.
      throw new Error(resolveApiErrorMessage(axiosError, "flight"));
    }
  },

  // Assets Page API
  getAssetsPageData: async (campaignId: string) => {
    try {
      const response = await apiClient.get<AssetsPageResponse>(
        `/assets/campaign/${campaignId}`
      );
      const data: AssetsPageData[] = response.data.data;
      set({ assetsPageData: data });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getAdCategoryData: async (campaignId?: string) => {
    try {
      let query = `with=publishers,ad_types,parameter_values`;

      if (campaignId) {
        query = query + `&campaign_id=${campaignId}`;
      }

      const response = await apiClient.get<AdCategoryDataResponse>(
        `/assets/ad-categories?${query}`
      );
      const data = response.data.data;
      const parameterDefinitions = response.data.parameter_definitions;
      const uiParametersByAdType = response.data.ui_parameters_by_ad_type;

      const assetsValues = (response.data as any).existing_assets_values;

      set({
        adCategoryData: data,
        parameterDefinitions,
        uiParametersByAdType,
        assetsValues,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  createAssets: async (assetsData: AssetsStoreRequest) => {
    try {
      const response = await apiClient.post("/assets", assetsData);
      const data = response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  updateAssets: async (assetsData: AssetsStoreRequest) => {
    try {
      const response = await apiClient.put(
        `/assets/${assetsData.campaign_id}`,
        assetsData
      );
      const data = response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      // Same as updateFlight: the ASSET_LOCKED reason arrives in `message`.
      throw new Error(resolveApiErrorMessage(axiosError, "asset"));
    }
  },

  // The wizard used to "delete" flights and assets in local state only, which
  // left the rows in the database and (for assets) broke the follow-up save.
  // Both now go through the real delete endpoints, which run the retention
  // pipeline server-side.
  deleteFlight: async (flightId: string) => {
    try {
      await apiClient.delete(`/flights/${flightId}`);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        "Failed to delete flight";
      throw new Error(message);
    }
  },

  deleteAsset: async (assetId: string) => {
    try {
      await apiClient.delete(`/assets/${assetId}`);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        "Failed to delete asset";
      throw new Error(message);
    }
  },

  restoreFlight: async (flightId: string) => {
    try {
      const response = await apiClient.post<RestoreResponse>(
        `/flights/${flightId}/restore`
      );
      return response.data.data;
    } catch (error) {
      throw toRestoreError(error, "Failed to restore flight");
    }
  },

  restoreAsset: async (assetId: string) => {
    try {
      const response = await apiClient.post<RestoreResponse>(
        `/assets/${assetId}/restore`
      );
      return response.data.data;
    } catch (error) {
      throw toRestoreError(error, "Failed to restore asset");
    }
  },

  //Campaign Summary API

  getCampaignSummary: async (campaignId: string) => {
    try {
      const response = await apiClient.get<CampaignSummaryResponse>(
        `/campaigns/${campaignId}/summary?include=assets`
      );
      const data = response.data.data;
      set({ campaignSummary: data });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getAssetsTable: async (
    campaignId: string,
    flightId?: string,
    sortColumn: string = "publisher",
    sortDirection: string = "asc",
    page: number = 1
  ) => {
    try {
      let url = `/campaigns/${campaignId}/assets-table?page=${page}&sort=${sortColumn}&direction=${sortDirection}`;

      if (flightId) {
        url += `&flight_id=${flightId}`;
      }

      const response = await apiClient.get<AssetsTableResponse>(url);
      const data = response.data;
      set({
        assetsTableData: data.data,
        assetsTableMeta: data.meta,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  downloadDeliveryTemplate: async (campaignId: string) => {
    try {
      const response = await apiClient.get(
        `/campaigns/${campaignId}/delivery-template`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `delivery_template_${campaignId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Failed to download template"
        : "Failed to download template";
      throw new Error(errorMessage);
    }
  },

  downloadCampaignAssets: async (campaignId: string) => {
    try {
      const response = await apiClient.get(
        `/campaigns/${campaignId}/assets/export`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `campaign_assets_${campaignId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Failed to export campaign assets"
        : "Failed to export campaign assets";
      throw new Error(errorMessage);
    }
  },

  downloadBaseTemplate: async () => {
    try {
      const response = await apiClient.get(`/campaigns/template/base`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `delivery_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Failed to download template"
        : "Failed to download template";
      throw new Error(errorMessage);
    }
  },

  parseCampaignTemplate: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post(
        `/campaigns/template/parse`,
        formData,
      );
      return response.data.data as ParsedTemplate;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Failed to read the file"
        : "Failed to read the file";
      throw new Error(errorMessage);
    }
  },

  // Move every draft asset version in the campaign to "briefed"
  briefCampaignAssets: async (campaignId: string) => {
    try {
      const response = await apiClient.post<BriefCampaignResponse>(
        `/asset-versions/campaign/${campaignId}/brief`
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message ||
          axiosError.response.data?.error ||
          "Failed to move assets to briefed"
        : "Failed to move assets to briefed";
      throw new Error(errorMessage);
    }
  },
}));

export default useCampaignStore;
